import { AlertCircle, DollarSign, Loader, TrendingUp } from "lucide-react";
import {
  Badge,
  Card,
  SelectBox,
  SelectBoxItem,
  Toggle,
  ToggleItem,
} from "@tremor/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { RangeType, ReportType } from "graphql/__generated__/globalTypes";
import { months, years } from "../../../utils";

import { getCompanyName } from "features/users";
import moment from "moment";
import { useAppSelector } from "app/hooks";
import { useRevenueBar } from "hooks/useRevenue";
import { useState } from "react";

const TotalRevenue = () => {
  const [currency, setCurrency] = useState("USD");
  const [tab, setTab] = useState("month");
  const [types, setTypes] = useState(ReportType.All);
  const reinsurer = useAppSelector(getCompanyName);

  const { data, loading, error } = useRevenueBar({
    args: {
      currency: currency,
      from: tab !== "month"
        ? "2018-01-01"
        : moment().startOf("year").format("YYYY-MM-DD"),
      rangeType: tab === "month" ? RangeType.Monthly : RangeType.Yearly,
      re_company_name: reinsurer,
      reportType: types,
      to: tab !== "month"
        ? moment().format("YYYY-MM-DD")
        : moment().endOf("year").format("YYYY-MM-DD"),
    }
  });

  const BROKER_COLORS = {
    visalre: "#4169E1", // Royal green
    kekre: "#32CD32",   // Lime Green
    iriskre: "#FF6347", // Tomato Red
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <Card>
        <div className="h-[500px] flex items-center justify-center">
          <div className="flex flex-col items-center text-gray-500">
            <Loader className="animate-spin h-8 w-8 text-green-500 mb-3" />
            <span className="text-sm font-medium">Loading revenue data...</span>
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="h-[500px] flex items-center justify-center">
          <div className="flex flex-col items-center text-red-500">
            <AlertCircle className="h-8 w-8 mb-3" />
            <span className="text-sm font-medium">Failed to load revenue data</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Reinsurance Revenue</h2>
            <p className="text-sm text-gray-500">
              Net premium earnings from broker network
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Toggle
              defaultValue={tab}
              color="gray"
              handleSelect={(value) => setTab(value)}
            >
              <ToggleItem value="month" text="Monthly" />
              <ToggleItem value="year" text="Yearly" />
            </Toggle>

            <SelectBox
              value={currency}
              onValueChange={setCurrency}
              placeholder="Currency"
              maxWidth="max-w-xl"
            >
              <SelectBoxItem value="USD" text="USD" />
              <SelectBoxItem value="EUR" text="EUR" />
              <SelectBoxItem value="GBP" text="GBP" />
              <SelectBoxItem value="GHS" text="GHS" />
            </SelectBox>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <Toggle
            value={types}
            color="green"
            handleSelect={(value) => setTypes(value)}
          >
            <ToggleItem value={ReportType.All} text="All Premiums" />
            <ToggleItem value={ReportType.Paid} text="Paid" />
            <ToggleItem value={ReportType.Outstanding} text="Outstanding" />
          </Toggle>

          <Badge
            icon={TrendingUp}
            color="emerald"
            tooltip="Total revenue growth trend"
            text={reinsurer || "All Reinsurers"}
          />

        </div>

        {/* Chart */}
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
              barSize={tab === "month" ? 30 : 40}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
                tickMargin={10}
              />
              <YAxis
                tickFormatter={(value) =>
                  new Intl.NumberFormat('en', {
                    notation: 'compact',
                    compactDisplay: 'short'
                  }).format(value)
                }
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value) => [formatCurrency(Number(value)), "Amount"]}
                labelFormatter={(label) => `Period: ${label}`}
                contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => (
                  <span className="text-xs text-gray-600">
                    {value.replace(' Broker', '')}
                  </span>
                )}
              />
              <Bar
                dataKey="visalre"
                name="Visal Re"
                fill={BROKER_COLORS.visalre}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="kekre"
                name="KEK Re"
                fill={BROKER_COLORS.kekre}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="iriskre"
                name="iRisk Re"
                fill={BROKER_COLORS.iriskre}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center text-xs text-gray-500 gap-1">
          <DollarSign className="h-3 w-3" />
          <span>All amounts displayed in {currency}</span>
        </div>
      </div>
    </div >
  );
};

export default TotalRevenue;